<script setup lang="ts">
import { onMounted, computed, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { useLazyAsyncData, useNuxtApp } from "#app";
import { useArticleCollectionStore } from "~/stores/article/articleCollection";
import { useArticlesPageStore } from "~/stores/page/articlesPage";
import { clearAdsenseBlocks } from "~/utils/adsense";

definePageMeta({
    alias: '/interesting-facts-page-:page',
});

const articlesPageStore = useArticlesPageStore();
const articleCollectionStore = useArticleCollectionStore();
const page = useRoute()?.params?.page !== undefined ? Number(useRoute()?.params.page) : 1;

await useLazyAsyncData('articlesPage', async () => articlesPageStore.fetch());
await useLazyAsyncData('articleFixTopCollection', async () => articleCollectionStore.fetchFixTopList('all'));
await useLazyAsyncData('articleCollection', async () => articleCollectionStore.fetchList(page));
if (typeof articleCollectionStore.error === "object") {
    throw articleCollectionStore.error;
}

if (typeof articlesPageStore.error === "object") {
    throw articlesPageStore.error;
}

const title = computed(() => articlesPageStore.title);
const textBeforeContent = computed(() => articlesPageStore.textBeforeContent ? useNuxtApp().marked().parse(articlesPageStore.textBeforeContent) : articlesPageStore.textBeforeContent);
const textAfterContent = computed(() => articlesPageStore.textAfterContent ? useNuxtApp().marked().parse(articlesPageStore.textAfterContent) : articlesPageStore.textAfterContent);
const fixTopArticles = computed(() => articleCollectionStore.fixTopList);
const articles = computed(() => articleCollectionStore.list);
const skeleton = computed(() => articleCollectionStore.skeleton);
const articlesError = computed(() => articleCollectionStore.error);
const pagination = computed(() => articleCollectionStore.pagination);

onMounted(() => {
    window.scrollTo(0, 0);
});

onUnmounted(() => {
    clearAdsenseBlocks();
    articleCollectionStore.clear();
});
</script>

<template>
    <section class="text-gray-600 body-font container">
        <div class="flex-col-reverse lg:flex-row flex items-center justify-between lg:gap-x-8 gap-x-6 mb-0 md:mb-12">
            <div class="w-full lg:w-1/2 relative flex flex-wrap items-center justify-between md:-mx-4">
                <CardArticleImageBackground v-if="fixTopArticles !== undefined"
                                            v-for="article in fixTopArticles"
                                            :key="article.slug"
                                            :title="article.title"
                                            :picture-url="article.picture"
                                            :detail-url="article.url"
                                            :date="article.date"
                />
                <CardArticleImageBackground v-else v-for="index in 4" :key="index" skeleton/>
            </div>
            <div v-if="title !== undefined" class="w-full lg:w-1/2 relative prose max-w-full leading-relaxed text-gray-500">
                <h1>{{ title }}</h1>
                <div v-if="textBeforeContent" class="mb-8 leading-relaxed font-medium text-lg" v-html="textBeforeContent"></div>
            </div>
            <div v-else class="w-full lg:w-1/2 relative">
                <span class="animate-pulse block rounded h-8 w-[60%] bg-gray-100 mb-8"></span>
                <span class="animate-pulse block rounded h-4 w-[30%] bg-gray-100"></span>
                <span class="animate-pulse block rounded h-4 w-[50%] bg-gray-100 mt-4"></span>
                <span class="animate-pulse block rounded h-4 w-[40%] bg-gray-100 mt-4 mb-8"></span>
            </div>
        </div>
<!--        <ClientOnly>
            <section class="hidden sm:flex container justify-center mb-16">
                <AdsenseMediaBigWidth/>
            </section>
            <section class="sm:hidden container flex justify-center mb-12">
                <AdsenseSmallHorizontal/>
            </section>
        </ClientOnly>-->
        <div class="md:flex md:flex-row">
            <aside class="mb-12 md:mb-initial md:w-2/5 lg:w-1/4 md:pr-8">
                <h2 class="hidden md:block sm:text-3xl text-2xl font-medium title-font mb-4 text-gray-900">Categories</h2>
                <NavAsideArticleCategories/>
                <ClientOnly>
                    <section class="hidden sm:block mt-12">
                        <AdsenseMedia/>
                    </section>
                </ClientOnly>
            </aside>

            <div class="md:w-3/5 lg:w-3/4 space-y-12">
                <div v-if="articles !== undefined && articles.length > 0" class="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <CardArticle v-for="article in articles"
                                 :key="article.slug"
                                 :title="article.title"
                                 :date="article.date"
                                 :picture-url="article.picture"
                                 :detail-url="article.url"
                                 :category="article.category"
                                 class="h-full"
                    />
                </div>
                <div v-else-if="articles === undefined && skeleton === true && !articlesError" class="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <CardArticle v-for="index in 10" :key="index" class="h-full" :skeleton="true"/>
                </div>
                <AlertErrorMessage v-else-if="articlesError" :message="articlesError" />
                <PaginationDefault v-if="pagination?.pageCount > 1"
                                   :page-count="pagination?.pageCount"
                                   :page="pagination?.page"
                                   base-path="/interesting-facts"
                />
                <ClientOnly>
                    <section class="hidden sm:flex container justify-center">
                        <AdsenseMediaBigWidth/>
                    </section>
                    <section class="sm:hidden container flex justify-center">
                        <AdsenseMedia/>
                    </section>
                </ClientOnly>
                <div v-if="textAfterContent !== undefined" class="prose w-full max-w-full mt-12 leading-relaxed text-gray-500" v-html="textAfterContent"></div>
                <div v-else class="mt-12 w-full">
                    <span class="animate-pulse block rounded h-4 w-[30%] bg-gray-100"></span>
                    <span class="animate-pulse block rounded h-4 w-[50%] bg-gray-100 mt-4"></span>
                    <span class="animate-pulse block rounded h-4 w-[40%] bg-gray-100 mt-4"></span>
                </div>
<!--                <div class="mx-auto grid max-w-2xl grid-cols-1 -mt-12 gap-x-8 gap-y-12 pt-16 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                    <article v-for="index in 12" class="flex max-w-xl flex-col items-start justify-between">
                        <div class="flex items-center gap-x-4 text-xs">
                            <time datetime="2020-03-16" class="text-gray-500">Mar 16, 2020</time>
                            <a href="#" class="relative z-10 rounded-full bg-gray-50 px-3 py-1.5 font-medium text-gray-600 hover:bg-gray-100">Marketing</a>
                        </div>
                        <div class="group relative">
                            <h3 class="mt-3 text-lg font-semibold leading-6 text-gray-900 group-hover:text-gray-600">
                                <a href="#">
                                    <span class="absolute inset-0"></span>
                                    Boost your conversion rate
                                </a>
                            </h3>
                            <p class="mt-5 line-clamp-3 text-sm leading-6 text-gray-600">Illo sint voluptas. Error voluptates culpa eligendi. Hic vel totam vitae illo. Non aliquid explicabo necessitatibus unde. Sed exercitationem placeat consectetur nulla deserunt vel. Iusto corrupti dicta.</p>
                        </div>
                    </article>
                </div>-->
            </div>
        </div>
    </section>
</template>